// Единое меню пользователя (аватар в шапке) — одинаково на всех экранах.
// Содержит: имя (редактируемое) + email + роль + тариф (если открыт проект) +
// выход. Только АДМИНУ — расширенный блок: переключатель ролей + Админ-панель.
import { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { LogOut, Shield, Check, Pencil, KeyRound } from 'lucide-react';
import { useAuth } from '@/state/auth';
import { api } from '@/api/client';
import { toast } from '@/state/toast';
import { tariffLabel } from '@/lib/tariff';
import { ChangePasswordModal } from '@/components/ChangePasswordModal';
import type { Project, Role, Tariff } from '@/types/api';

const ROLE_LABEL_FULL: Record<string, string> = {
  curator: 'Куратор', expert: 'Эксперт', gip: 'ГИП', admin: 'Админ', customer: 'Заказчик',
};

const ROLE_OPTIONS: { id: Role; label: string; sub: string; dot: string }[] = [
  { id: 'curator', label: 'Куратор',  sub: 'объекта',        dot: '#F59E0B' },
  { id: 'expert',  label: 'Эксперт',  sub: 'по направлению',  dot: '#0A1628' },
  { id: 'gip',     label: 'ГИП',      sub: 'главный инженер', dot: '#10B981' },
  { id: 'admin',   label: 'Админ',    sub: 'организации',      dot: '#EF4444' },
];

/** projectId — опционально: если открыт проект, показываем плашку тарифа. */
export function AvatarMenu({ projectId }: { projectId?: string }) {
  const [open, setOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [nameInput, setNameInput] = useState('');
  const [nameSaving, setNameSaving] = useState(false);
  const nameRef = useRef<HTMLInputElement>(null);

  const user = useAuth((s) => s.user);
  const activeRole = useAuth((s) => s.activeRole);
  const switchRole = useAuth((s) => s.switchRole);
  const logout = useAuth((s) => s.logout);
  const updateUser = useAuth((s) => s.updateUser);

  // Тариф текущего проекта — только когда проект открыт.
  const projectQ = useQuery<Project>({
    queryKey: ['project', projectId],
    queryFn: () => api.get<Project>(`/projects/${projectId}`),
    enabled: !!projectId,
    staleTime: 30_000,
  });

  if (!user) return null;
  const initials = user.full_name.split(/\s+/).filter(Boolean).slice(0, 2).map((s) => s[0]?.toUpperCase()).join('');
  const isAdmin = user.default_role === 'admin';
  const roleLabel = ROLE_LABEL_FULL[activeRole || ''] || activeRole || '';
  const projectTariff: Tariff = (projectQ.data as { tariff?: Tariff } | undefined)?.tariff ?? null;

  const startEditName = () => {
    setNameInput(user.full_name);
    setEditingName(true);
    setTimeout(() => nameRef.current?.focus(), 50);
  };

  const saveName = async () => {
    const trimmed = nameInput.trim();
    if (!trimmed || trimmed === user.full_name) { setEditingName(false); return; }
    setNameSaving(true);
    try {
      await api.post('/auth/update-profile', { full_name: trimmed });
      updateUser({ full_name: trimmed });
      toast.success('Имя обновлено');
      setEditingName(false);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Не удалось сохранить');
    } finally {
      setNameSaving(false);
    }
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="rounded-pill bg-surface border border-border flex items-center justify-center hover:border-ink/30"
        style={{ width: 32, height: 32, cursor: 'pointer' }}
        aria-label={`Профиль: ${user.full_name}`}
      >
        <span style={{ fontWeight: 600, fontSize: 12, color: '#0A1628' }}>{initials}</span>
      </button>
      {open && (
        <>
          <div className="fixed inset-0" style={{ zIndex: 35 }} onClick={() => { setOpen(false); setEditingName(false); }} />
          <div
            className="absolute bg-white rounded-card flex flex-col"
            style={{
              top: 'calc(100% + 8px)', right: 0, width: 280,
              border: '1px solid #E5E7EB',
              boxShadow: '0 16px 48px rgba(10,22,40,.16)',
              zIndex: 40, overflow: 'hidden',
            }}
          >
            <div style={{ padding: '14px 16px', borderBottom: '1px solid #E5E7EB' }}>
              {editingName ? (
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <input
                    ref={nameRef}
                    value={nameInput}
                    onChange={(e) => setNameInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') saveName(); if (e.key === 'Escape') setEditingName(false); }}
                    onClick={(e) => e.stopPropagation()}
                    maxLength={200}
                    style={{ flex: 1, fontSize: 13, fontWeight: 600, padding: '4px 8px', border: '1px solid #CBD5E1', borderRadius: 6, color: '#0A1628', outline: 'none' }}
                  />
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); saveName(); }}
                    disabled={nameSaving}
                    style={{ fontSize: 11, fontWeight: 600, padding: '4px 8px', borderRadius: 6, background: '#0A1628', color: 'white', border: 'none', cursor: 'pointer', whiteSpace: 'nowrap' }}
                  >{nameSaving ? '…' : 'Сохранить'}</button>
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#0A1628', flex: 1 }}>{user.full_name}</div>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); startEditName(); }}
                    title="Изменить имя"
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: '#94A3B8', lineHeight: 1 }}
                  ><Pencil size={13} /></button>
                </div>
              )}
              <div className="text-muted mt-0.5" style={{ fontSize: 12 }}>{user.email}</div>
              {user.expert_section && <div className="mt-1" style={{ fontSize: 11, fontWeight: 500, color: '#B45309' }}>Раздел: {user.expert_section}</div>}
              {/* Роль (всегда) + тариф (если открыт проект). */}
              <div className="flex items-center mt-2" style={{ gap: 8, flexWrap: 'wrap' }}>
                {roleLabel && (
                  <span className="rounded-pill" style={{ padding: '2px 9px', fontSize: 11, fontWeight: 600, background: '#F3F4F6', color: '#374151' }}>
                    Роль: {roleLabel}
                  </span>
                )}
                {projectId && (
                  <span className="rounded-pill" style={{ padding: '2px 9px', fontSize: 11, fontWeight: 600, background: 'rgba(16,185,129,.10)', color: '#0F6E56' }}>
                    {tariffLabel(projectTariff)}
                  </span>
                )}
              </div>
            </div>
            {/* Переключатель ролей — ТОЛЬКО админу (расширенный режим). */}
            {isAdmin && (
            <div style={{ padding: 8 }}>
              <div className="text-muted" style={{ padding: '6px 8px', fontSize: 11, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase' }}>Роль</div>
              {ROLE_OPTIONS.map((r) => {
                const active = activeRole === r.id;
                return (
                  <button
                    key={r.id}
                    type="button"
                    onClick={() => { switchRole(r.id); setOpen(false); toast.info(`Роль: ${r.label}`, 2500); }}
                    className="w-full text-left rounded-btn flex items-center"
                    style={{
                      padding: '8px 10px', gap: 10,
                      background: active ? '#F3F4F6' : 'transparent',
                      border: 'none', cursor: 'pointer',
                      fontSize: 13, fontWeight: active ? 600 : 500, color: '#0A1628',
                    }}
                  >
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: r.dot }} />
                    <span style={{ flex: 1 }}>{r.label}</span>
                    <span className="text-muted" style={{ fontSize: 11 }}>{r.sub}</span>
                    {active && <Check size={14} stroke="#10B981" />}
                  </button>
                );
              })}
            </div>
            )}
            <div style={{ borderTop: '1px solid #E5E7EB', padding: 8 }}>
              {/* Админ-панель — только админу (расширенный режим). */}
              {isAdmin && (
                <Link
                  to="/admin"
                  onClick={() => setOpen(false)}
                  className="w-full text-left rounded-btn flex items-center"
                  style={{ padding: '8px 10px', gap: 10, fontSize: 13, fontWeight: 500, color: '#0A1628', textDecoration: 'none' }}
                >
                  <Shield size={14} stroke="#6B7280" /> Админ-панель
                </Link>
              )}
              <button
                type="button"
                onClick={() => { setOpen(false); setPwOpen(true); }}
                className="w-full text-left rounded-btn flex items-center"
                style={{ padding: '8px 10px', gap: 10, fontSize: 13, fontWeight: 500, color: '#0A1628', background: 'none', border: 'none', cursor: 'pointer' }}
              >
                <KeyRound size={14} stroke="#6B7280" /> Сменить пароль
              </button>
              <button
                type="button"
                onClick={() => { setOpen(false); logout(); toast.info('Сессия завершена'); }}
                className="w-full text-left rounded-btn flex items-center"
                style={{ padding: '8px 10px', gap: 10, fontSize: 13, fontWeight: 500, color: '#6B7280', background: 'none', border: 'none', cursor: 'pointer' }}
              >
                <LogOut size={14} stroke="#6B7280" /> Выйти
              </button>
            </div>
          </div>
        </>
      )}
      {pwOpen && <ChangePasswordModal onClose={() => setPwOpen(false)} />}
    </div>
  );
}
